import React, { useMemo } from 'react';
import { Collapse, Divider, Tag } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { UserStatusType } from '../../../../store/auth';
import { UserRoleDisplay, UserStatusDisplay } from '../../../../enums/user';
import { dateTimeFormat } from '../../../../contstant';
import { createDefaultHiddenColumns, getSorterParams, queryFilterParams } from '../../../../utils';
import { useMessageError } from '../../../../hooks/common';
import Table from '../../../Common/Table';
import { UserInviteTableRow } from '../../../../hooks/api/inviteUsers';
import { useInviteUsersContext } from '../../../../context/inviteUsersContext';

export enum UserStatusColor {
  'active' = 'green',
  // 'pending' = 'blue',
  'pending' = 'processing',
  'deactivated' = 'red',
  'archived' = 'default',
}

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || 'rgba(0,0,0,.15)';
}

const columns: ProColumns<UserInviteTableRow>[] = [
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    sorter: true,
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'roles', // key - for filters. dataIndex - for display.
    sorter: true,
    valueEnum: UserRoleDisplay,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    sorter: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Tag color={getUserStatusColor(record.status)} className="capitalize-first-letter">{record.status}</Tag>
    ),
  },
  {
    title: 'Received emails',
    dataIndex: 'receivedEmails emails',
    key: 'receivedEmails',
    sorter: false,
    hideInSearch: true,
    width: 240,
    render: (_, record) => (record?.sentEmails?.length > 0 ? (
      <Collapse
        size="small"
        items={[{
          key: '1',
          label: 'See received emails',
          children: (
            <div>
              {(record.sentEmails || []).map((item, index, arr) => (
                <div key={crypto.randomUUID()}>
                  <div title="Action">
                    <b>Action:</b>
                    {' '}
                    {item.action}
                  </div>
                  <div title="Type">
                    <b>Type:</b>
                    {' '}
                    {item.type}
                  </div>
                  <div>{dayjs(item.createdAt).format(dateTimeFormat)}</div>
                  {index !== arr.length - 1 && <Divider style={{ margin: '12px 0' }} />}
                </div>
              ))}
            </div>
          ),
        }]}
      />
    ) : '-'), // 'No sent emails'
  },
  {
    title: 'Created at',
    dataIndex: 'createdAt',
    key: 'createdAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: 'Updated at',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
];

function ToolBar() {
  const navigate = useNavigate();

  return [
    /* <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        navigate('create');
      }}
    >
      Add
    </Button>, */
  ];
}

export default function InviteUsersTable(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { usersInviteTableGet, tableReloadTriggerKey, setTableReloadTrigger } = useInviteUsersContext();

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserInviteTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return usersInviteTableGet.fetch({
      ...newParams,
      tariffNames: newParams.tariffNames ? newParams?.tariffNames?.split(',') : undefined,
      roles: newParams.roles ? newParams?.roles?.split(',') : undefined,
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({
          data: (data || []).map(((item) => ({
            ...item,
            key: item.id,
          }))),
          success: true,
          total,
        });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  useMessageError([usersInviteTableGet]);

  const defaultHiddenColumns = useMemo(() => createDefaultHiddenColumns(
    ['createdAt', 'updatedAt'],
  ), []);

  return (
    <Table<UserInviteTableRow>
      key={tableReloadTriggerKey} // {tableKey}
      resetKey={() => setTableReloadTrigger(Date.now())} // {() => setTableKey(Date.now())}
      className={clsx('transparent', 'table-no-padding')}
      headerTitle="List of users"
      toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns}
      actions={[
        ['edit', ({ record }) => ({
          title: 'Edit user',
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            navigate(`/users/${record.id}/edit`);
          },
        })],
      ]}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
        style: {
          display: 'none',
        },
      }}
      options={{
        // search: {
        //   name: 'search',
        // },
      }}
      columnsState={{
        persistenceKey: 'pro-table-users',
        persistenceType: 'localStorage',
        defaultValue: defaultHiddenColumns,
      }}
    />
  );
}
